<template>
  <div class="p-template-status__loader" v-if="loading">
    <VLoadingSpinner size="large" />
  </div>
  <div class="p-template-status g-page" v-else>
    <div class="g-page__header flex flex-row items-center">
      <div
        class="mr-base">
        <RouterLink
          :to="backButtonTo">
          <VButton
            :rounded="true"
            type="outline-1"
            @click="goBackToTemplates">
            <ArrowLeftIcon class="back-icon" />
            <span class="ml-sm xs:hidden sm:inline">
              {{ $t('functional.back') }}
            </span>
          </VButton>
        </RouterLink>
      </div>
      <div>
        <h2 data-hj-suppress>{{ template.title }}</h2>
        <label data-hj-suppress class="mt-4 text-14 font-normal" v-if="canEditResource(template)">

          <span v-if="sharedWithEveryone">
            {{ $t('sharing.sharedWithEveryone') }} |
          </span>
          <span v-else>
            {{ $tc('sharing.sharedWithCount', null, { groupCount: sharedGroups.length, userCount: sharedUsers.length }) }} |
          </span>
          <a
            @click="$refs.templateSharingModal.open()"
            data-ec="template_status"
            data-et="anchor_open_edit_sharing_modal"
          >
            {{ $t('template.editSharing') }}
          </a>
        </label>
      </div>
    </div>
    <VGrid>
      <div slot="gridItem" type="9">
        <div class="g-page__row">
          <img
          v-for="image in template.images"
          :key="image"
          class="p-template-status__preview"
          src="@/assets/loading.png"
          v-image-loader="{
              src: `${image}`,
              onerror: require('@/assets/error.png'),
          }"
          data-hj-suppress />
        </div>
      </div>
      <div slot="gridItem" type="3">
        <VStickyColumn
          relativeElementSelector=".o-grid__item--relative-sticky-column"
          pageClass=".p-template-status">
          <div class="g-page__row p-template-status__controls">

            <VButton
              v-if="hasTemplatesEnvelopes"
              type="cta-1"
              :fullwidth="true"
              :disabled="!hasTemplatesEnvelopes"
              :to="{ name: 'TemplateAssociatedEnvelopes', params: { templateId: template.fingerprint }}"
              data-ec="template_status"
              data-et="associated_envelopes_button">
              <EnvelopeIcon />
              <span>{{ $t('template.viewAssociatedEnvelopes.button') }}</span>
            </VButton>
            <ElTooltip
              v-else
              class="item"
              effect="dark"
              :content="$t('template.viewAssociatedEnvelopes.precondition')"
              placement="left">
              <VButton
                type="cta-1"
                :fullwidth="true"
                :disabled="true"
                :to="{ name: 'TemplateAssociatedEnvelopes', params: { templateId: template.fingerprint }}"
                data-ec="template_status"
                data-et="Associated Envelopes Button">
                <EnvelopeIcon />
                <span>{{ $t('template.viewAssociatedEnvelopes.button') }}</span>
              </VButton>
            </ElTooltip>

            <VButton
              v-if="canEditResource(template)"
              type="outline-1"
              :fullwidth="true"
              :to="{ name: 'TemplateEdit', params: { templateId: template.fingerprint }}"
              data-test="template_status-btn_edit_template"
              data-ec="template_status"
              data-et="btn_edit_template">
              <PencilIcon />
              <span>{{ $t('template.editTemplate') }}</span>
            </VButton>

            <VButton
              v-if="canEditResource(template)"
              type="outline-1"
              :fullwidth="true"
              @click="$refs.templateSharingModal.open()"
              data-test="template_status-btn_open_edit_sharing_modal"
              data-ec="template_status"
              data-et="btn_open_edit_sharing_modal">
              <UserIcon />
              <span>{{ $t('template.editSharing') }}</span>
            </VButton>

            <VButton
              v-if="canEditResource(template)"
              type="outline-1"
              :fullwidth="true"
              @click="copyTemplate"
              data-test="template_status-copy_template_button"
              data-ec="template_status"
              data-et="copy_template_button">
              <CloneIcon />
              <span>{{ $t('template.copyTemplate') }}</span>
            </VButton>

            <VButton
              v-if="canEditResource(template)"
              type="outline-3"
              :fullwidth="true"
              @click="openModal('template-replace-modal')"
              data-test="template_status-replace_template_button"
              data-ec="template_status"
              data-et="replace_template_button">
              <FileTextIcon />
              <span>{{ $t('template.replaceTemplate.replaceTemplate') }}</span>
            </VButton>

            <VButton
              v-if="template.parties && template.parties.length === 1"
              type="outline-3"
              :fullwidth="true"
              @click="openModal('template-widget-modal')"
              data-ec="template_status"
              data-et="create_widget_button">
              <CogsIcon />
              <span>{{ $t('templates.widgets.createWidget') }}</span>
            </VButton>
            <ElTooltip
              v-else
              class="item"
              effect="dark"
              :content="$t('templates.widgets.precondition')"
              placement="left">
              <VButton
                type="outline-3"
                :fullwidth="true"
                @click="openModal('template-widget-modal')"
                :disabled="true"
                data-ec="template_status"
                data-et="create_widget_button">
                <CogsIcon />
                <span>{{ $t('templates.widgets.createWidget') }}</span>
              </VButton>
            </ElTooltip>

            <VButton
              type="success"
              :fullwidth="true"
              @click="downloadTemplate()"
              :disabled="isDownloading"
              :class="{ 'cursor-wait': isDownloading }"
              data-test="template_status-download_button"
              data-ec="template_status"
              data-et="download_button">
              <DownloadIcon />
              <span>{{ $t('functional.download') }}</span>
            </VButton>

            <VButton
              v-if="canEditResource(template)"
              type="error"
              :fullwidth="true"
              @click="openModal('template-remove-modal')"
              data-test="template_status-delete_template_button"
              data-ec="template_status"
              data-et="delete_template_button">
              <TrashIcon />
              <span>{{ $t('template.deleteTemplate') }}</span>
            </VButton>

          </div>
          <h3>{{ $t('template.creator') }}</h3>
          <p data-hj-suppress>{{ template.user ? template.user.name : '' }}</p>
        </VStickyColumn>
      </div>
    </VGrid>

    <VDynamicLoader
      viewURL="modals/template/Remove"
      :data="{ template }" />

    <VDynamicLoader
      viewURL="modals/template/Replace"
      :data="{ template }" />

    <VDynamicLoader
      viewURL="modals/template/Widget"
      :data="{ template }" />

    <TemplateSharingModal
      :template="template"
      ref="templateSharingModal" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import VButton from '@/components/deprecated/atoms/Button.vue';
import VLoadingSpinner from '@/components/deprecated/atoms/LoadingSpinner.vue';
import VStickyColumn from '@/components/deprecated/molecules/StickyColumn.vue';
import VGrid from '@/components/deprecated/organisms/Grid.vue';
import TemplateSharingModal from '@/views/modals/template/TemplateSharingModal.vue';
import download from '@/utils/download';
import VDynamicLoader from '@/components/DynamicLoader.vue';
import {
  ArrowLeftIcon,
  CloneIcon,
  CogsIcon,
  DownloadIcon,
  EnvelopeIcon,
  FileTextIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
} from '@/components/icons/fontIcons';

export default {
  name: 'TemplateStatus',
  components: {
    VButton,
    VLoadingSpinner,
    VStickyColumn,
    VGrid,
    TemplateSharingModal,
    VDynamicLoader,
    ArrowLeftIcon,
    CloneIcon,
    CogsIcon,
    DownloadIcon,
    EnvelopeIcon,
    FileTextIcon,
    PencilIcon,
    TrashIcon,
    UserIcon,
  },
  data() {
    return {
      loading: true,
      isDownloading: false,
      backButtonTo: { name: 'Templates-Page1' },
    };
  },
  async mounted() {
    const fingerprint = this.$route.params.templateId;
    await Promise.all([
      this.getTemplatesEnvelopes({
        templateFingerprint: fingerprint,
        pageLimit: 1,
        page: 1,
      }),
      this.getTemplate({ id: fingerprint }),
    ]);
    this.loading = false;
  },
  computed: {
    ...mapGetters({
      template: 'template/selected',
      templatesEnvelopes: 'templatesEnvelopes/pagedResults',
      user: 'user/user',
      canEditResource: 'template/canEditResource',
    }),
    hasTemplatesEnvelopes() {
      return this.templatesEnvelopes.length > 0;
    },
    sharedGroups() {
      return this.template.sharing?.groups || [];
    },
    sharedUsers() {
      return this.template.sharing?.users || [];
    },
    sharedWithEveryone() {
      return this.template.everyone_permission;
    },
  },
  methods: {
    ...mapActions({
      getTemplate: 'template/GET',
      getTemplatesEnvelopes: 'templatesEnvelopes/PAGINATE',
      performCopy: 'template/COPY',
    }),
    openModal(name) {
      this.$bus.$emit('closeModal');
      this.$bus.$emit('toggleModal', { name, active: true });
    },
    async copyTemplate() {
      this.loading = true;
      try {
        await this.performCopy(this.template.fingerprint);
        this.$bus.$emit('renderGlobalMessage', ({
          message: this.$t('message.successfullyCopied', { name: 'template' }),
          icon: 'check',
          type: 'success',
        }));
      } catch (e) {
        this.$bus.$emit('renderGlobalMessage', ({
          message: this.$t('message.fail.creating', { name: 'copy' }),
          icon: 'times',
          type: 'error',
        }));
        throw e;
      } finally {
        this.loading = false;
      }
    },
    async downloadTemplate() {
      this.isDownloading = true;

      await download({
        url: 'download/template',
        params: {
          fingerprint: this.template.fingerprint,
        },
        fileName: `${this.template.title}`,
      });

      this.isDownloading = false;
    },
    async goBackToTemplates() {
      await this.$router.push({ path: this.backButtonTo.path, query: this.backButtonTo.query });
    },
  },
  /**
   * This method is added in order to be able to return
   * to the *actual* previous template page ie. if they
   * we're on page 50 of 100, they'd return to page 50.
   *
   * If they went to the template status page directly,
   * the back button would return by default to the first
   * templates listing page.
   */
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (from.name === 'Templates') {
        // eslint-disable-next-line no-param-reassign
        vm.backButtonTo = from;
      }
    });
  },
};
</script>

<style scoped>
.back-icon {
  margin-right: 0 !important;
}

.p-template-status__controls .a-button {
  margin-bottom: var(--spacing-small);
  display: block;
  white-space: pre;
}

.p-template-status__preview {
  border: 1px solid var(--color-border);
  border-radius: 9px;
  box-shadow: 0px 0px 5px -2px var(--color-border);
  margin-bottom: 1em;
}

.p-template-status--header-details ::v-deep .a-label {
  margin-right: var(--spacing-small);
  padding: var(--spacing-xs) var(--spacing-base);
}

.p-template-status ::v-deep .m-table__td--type {
  text-transform: capitalize;
}

@media (max-width: 1020px) {
  .p-template-status__preview {
    margin-top: 0;
  }
}
</style>
